import { useEffect, useMemo, useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { useQuery } from '@tanstack/react-query'
import { icons } from 'lucide-react'
import { monadClient } from '../../api/client'
import { TypeIcon } from '../../sidebar/TypeIcon'
import { registerHintWidget, registerWidget, type CellContentProps, type CellEditorProps } from '../registry'
import { controlChrome, editorBehavior } from '../editorChrome'
import { Button } from '../Button'
import { ISO_3166_1_ALPHA2 } from '../subFieldKinds'

type IconOption = { payload: string; search: string }

const ICON_OPTIONS: Record<string, readonly IconOption[]> = {
  lucide: Object.keys(icons).sort().map((name) => ({ payload: name, search: name })),
  flag: [...ISO_3166_1_ALPHA2].sort().map((code) => ({ payload: code, search: code })),
  emoji: [
    ['🌍', 'world globe earth'], ['⚡', 'lightning energy electricity'],
    ['🔥', 'fire flame heat'], ['💧', 'water droplet'], ['☀️', 'sun solar'],
    ['❄️', 'snowflake cold'], ['🏢', 'office building'], ['🏷️', 'tag label'],
  ].map(([payload, search]) => ({ payload, search })),
}

export function IconContent({ value }: CellContentProps) {
  const icon = typeof value === 'string' ? value : null
  return icon ? (
    <span className="inline-flex items-center gap-1.5">
      <TypeIcon icon={icon} name="Icon" />
      <span className="font-mono text-xs text-muted-foreground">{icon}</span>
    </span>
  ) : <span className="text-muted-foreground">—</span>
}

export function IconEditor({ value, onCommit, onCancel, context, slug }: CellEditorProps) {
  const behavior = editorBehavior(context)
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const current = typeof value === 'string' ? value : ''
  const { data: subtypes } = useQuery({
    queryKey: ['subtypes', slug],
    queryFn: () => monadClient.listSubtypes(slug!),
    enabled: Boolean(slug),
    staleTime: 300_000,
  })
  const iconSchema = subtypes?.find((subtype) => subtype.name === 'icon')?.value_schema
  const servedSchemes = iconSchema?.['x-schemes'] as string[] | undefined
  const schemeLabels = iconSchema?.['x-scheme-labels'] as Record<string, string> | undefined
  const derivedOnly = iconSchema?.['x-derived-only-schemes'] as string[] | undefined
  const pickerSchemes = useMemo(
    () => (servedSchemes ?? []).filter((scheme) => !(derivedOnly ?? []).includes(scheme)),
    [derivedOnly, servedSchemes],
  )
  useEffect(() => {
    if (!tab || !pickerSchemes.includes(tab)) setTab(pickerSchemes[0] ?? null)
  }, [pickerSchemes, tab])
  const options = useMemo(() => {
    if (!tab) return []
    const needle = query.trim().toLowerCase()
    return (ICON_OPTIONS[tab] ?? [])
      .filter((option) => !needle || option.search.toLowerCase().includes(needle) || option.payload.toLowerCase().includes(needle))
      .map((option) => `${tab}:${option.payload}`)
  }, [query, tab])

  const prefixedQuery = () => {
    const payload = query.trim()
    return tab && payload ? `${tab}:${payload}` : null
  }

  const commit = (next: string) => {
    if (behavior.controlledOpen) setOpen(false)
    onCommit(next)
  }

  return (
    <Popover.Root open={behavior.controlledOpen ? open : undefined} defaultOpen={!behavior.controlledOpen}
      onOpenChange={(next) => {
        if (behavior.controlledOpen) setOpen(next)
        if (!next && behavior.dismissCancels) onCancel()
      }}>
      <Popover.Trigger asChild>
        <button type="button" data-testid="cell-editor"
          className={`flex w-full items-center gap-2 rounded-sm bg-background px-2 py-1.5 text-left text-sm outline-none ${controlChrome(context)}`}
          onClick={behavior.controlledOpen ? () => setOpen(true) : undefined}>
          <TypeIcon icon={current || null} name="Icon" servedSchemes={servedSchemes} />
          <span className="min-w-0 flex-1 truncate">{current || 'Choose icon'}</span>
          <span aria-hidden>⌄</span>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="start" sideOffset={4} onOpenAutoFocus={(event) => event.preventDefault()}
          className="z-50 w-72 rounded-float border border-border bg-background p-2 shadow-float">
          <div className="mb-2 flex flex-wrap gap-1" role="tablist" aria-label="Icon source">
            {pickerSchemes.map((nextTab) => (
              <Button key={nextTab} type="button" variant={tab === nextTab ? 'outline' : 'quiet'} size="xs"
                role="tab" aria-selected={tab === nextTab} onClick={() => setTab(nextTab)}>
                {schemeLabels?.[nextTab] ?? nextTab}
              </Button>
            ))}
          </div>
          <input autoFocus={behavior.autoFocus} data-testid="icon-search" value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && prefixedQuery()) { event.preventDefault(); commit(prefixedQuery()!) }
              if (event.key === 'Escape') {
                event.preventDefault()
                if (behavior.escapeIsLocal) event.stopPropagation()
                if (behavior.controlledOpen) setOpen(false); else onCancel()
              }
            }}
            placeholder={tab ? `Search ${(schemeLabels?.[tab] ?? tab).toLowerCase()}…` : 'Loading icon sources…'}
            className={`mb-2 w-full rounded-sm bg-background px-2 py-1.5 text-sm outline-none ${controlChrome('form')}`} />
          <div className="grid max-h-48 grid-cols-5 gap-1 overflow-auto">
            {options.map((option) => (
              <Button key={option} type="button" variant="bare" size="none" title={option}
                className="flex h-9 w-9 items-center justify-center rounded-sm hover:bg-muted"
                onClick={() => commit(option)}><TypeIcon icon={option} name="Icon" servedSchemes={servedSchemes} /></Button>
            ))}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

const iconWidget = { read: IconContent, write: IconEditor, defaultWidth: 180 }
registerWidget('icon', iconWidget)
registerHintWidget('icon', iconWidget)
